import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import './Dealerships.css';
import DropdownManager from './DropdownManager';
import ConfirmModal from './ConfirmModal';

function Dealerships({ user, managedOwner }) {
  const [dealerships, setDealerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingDealership, setEditingDealership] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '' });

  useEffect(() => {
    fetchDealerships();
  }, [user, managedOwner]);

  const fetchDealerships = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
      let url = `${apiUrl}/api/owners/dealerships`;
      
      // If admin is managing an owner, pass the owner ID
      if (managedOwner?.id) {
        url += `?ownerId=${managedOwner.id}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDealerships(data.dealerships || []);
      } else {
        setError('Failed to load dealerships');
      }
    } catch (error) {
      console.error('Failed to fetch dealerships:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditDealership = (dealership) => {
    setEditingDealership({
      id: dealership.id,
      name: dealership.name,
      location: dealership.location,
      performanceYearMode: dealership.performanceYearMode || 'CALENDAR',
      fiscalYearStartMonth: dealership.fiscalYearStartMonth || 1,
      fiscalYearStartDay: dealership.fiscalYearStartDay || 1,
      talonDatasetKey: dealership.talonDatasetKey || '',
      betaFeatures: dealership.betaFeatures || {},
      showUnclosedFI: dealership.showUnclosedFI !== false,
    });
    setShowEditModal(true);
  };

  const handleSaveDealership = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/owners/dealerships/${editingDealership.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            name: editingDealership.name,
            location: editingDealership.location,
            performanceYearMode: editingDealership.performanceYearMode,
            fiscalYearStartMonth: Number(editingDealership.fiscalYearStartMonth),
            fiscalYearStartDay: Number(editingDealership.fiscalYearStartDay),
            talonDatasetKey: editingDealership.talonDatasetKey || null,
            showUnclosedFI: editingDealership.showUnclosedFI
          })
        }
      );

      if (response.ok) {
        await fetchDealerships();
        setShowEditModal(false);
        setEditingDealership(null);
      } else {
        const data = await response.json();
        setAlertModal({ isOpen: true, title: 'Error', message: data.error || 'Failed to update dealership' });
      }
    } catch (error) {
      console.error('Failed to update dealership:', error);
      setAlertModal({ isOpen: true, title: 'Error', message: 'Network error. Please try again.' });
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder the dealerships array
    const newDealerships = [...dealerships];
    const [draggedItem] = newDealerships.splice(draggedIndex, 1);
    newDealerships.splice(dropIndex, 0, draggedItem);

    // Update display order for all dealerships
    const updates = newDealerships.map((d, index) => ({
      id: d.id,
      displayOrder: index
    }));

    // Optimistically update UI
    setDealerships(newDealerships);
    setDraggedIndex(null);
    setDragOverIndex(null);

    // Save to backend
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/owners/dealerships/reorder`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ updates })
        }
      );

      if (!response.ok) {
        // Revert on error
        await fetchDealerships();
        setAlertModal({ isOpen: true, title: 'Error', message: 'Failed to update order. Please try again.' });
      }
    } catch (error) {
      console.error('Failed to save dealership order:', error);
      await fetchDealerships();
      setAlertModal({ isOpen: true, title: 'Error', message: 'Network error. Please try again.' });
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  if (loading) {
    return (
      <div className="view-header">
        <h1 className="view-title">Dealerships</h1>
        <p className="view-subtitle">Loading dealership data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="view-header">
          <h1 className="view-title">Dealerships</h1>
          <p className="view-subtitle">Manage your dealership network</p>
        </div>
        <div className="content-card">
          <p style={{ color: '#ef4444' }}>{error}</p>
          <button 
            onClick={fetchDealerships} 
            className="submit-btn" 
            style={{ marginTop: '16px', width: 'auto' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="view-header">
        <h1 className="view-title">Dealerships</h1>
        <p className="view-subtitle">Manage your dealership network</p>
      </div>

      {dealerships.length > 1 && (
        <div style={{
          background: 'rgba(255, 122, 0, 0.1)',
          border: '1px solid rgba(255, 122, 0, 0.3)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: 'rgba(255, 255, 255, 0.9)',
          fontSize: '14px'
        }}>
          <strong>Tip:</strong> Drag and drop dealership cards to change the order they appear throughout the application.
        </div>
      )}

      <div className="dealerships-grid">
        {dealerships.length === 0 ? (
          <div className="content-card">
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
              No dealerships found. Contact support to set up your first dealership.
            </p>
          </div>
        ) : (
          dealerships.map((dealership, index) => (
            <div
              key={dealership.id}
              className={`dealership-card ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
              draggable={true}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              <div className="dealership-card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                  <div style={{ cursor: 'grab', color: 'rgba(255, 122, 0, 0.6)', fontSize: '16px' }} title="Drag to reorder">
                    ⠿
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <div className="dealership-name">{dealership.name}</div>
                      {dealership.demoMode && (
                        <span style={{
                          background: 'rgba(59, 130, 246, 0.1)',
                          border: '1px solid rgba(59, 130, 246, 0.3)',
                          borderRadius: '4px',
                          padding: '2px 8px',
                          fontSize: '11px',
                          fontWeight: '600',
                          color: 'rgba(147, 197, 253, 1)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          flexShrink: 0
                        }} title="Demo mode enabled - shows sample data">
                          DEMO
                        </span>
                      )}
                    </div>
                    <div className="dealership-location">{dealership.location}</div>
                  </div>
                </div>
                <button
                  className="edit-dealership-btn"
                  onClick={() => handleEditDealership(dealership)}
                  title="Edit dealership"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
              </div>
              <div className="dealership-card-body">
                <div className="dealership-stat">
                  <span className="stat-label">Status</span>
                  <span className={`status-badge ${dealership.status?.toLowerCase() || 'active'}`}>
                    {dealership.status || 'Active'}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Edit Dealership Modal */}
      {showEditModal && editingDealership && (
        <div className="dealership-modal-overlay">
          <div className="dealership-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Dealership</h2>
            <form onSubmit={handleSaveDealership}>
              <div className="form-group">
                <label>Dealership Name *</label>
                <input
                  type="text"
                  required
                  value={editingDealership.name}
                  onChange={(e) => setEditingDealership({
                    ...editingDealership,
                    name: e.target.value
                  })}
                />
              </div>

              <div className="form-group">
                <label>Location *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g., Denver, CO"
                  value={editingDealership.location}
                  onChange={(e) => setEditingDealership({
                    ...editingDealership,
                    location: e.target.value
                  })}
                />
              </div>

              {/* Talon Integration — only shown when admin has enabled the add-on */}
              {editingDealership.betaFeatures?.talonApi && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Talon Dealer ID
                    <span style={{ fontSize: '11px', fontWeight: '600', color: 'rgba(139,92,246,0.9)', background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.25)', borderRadius: '3px', padding: '1px 5px', letterSpacing: '0.04em' }}>ADD-ON</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your Talon Dealer ID"
                    value={editingDealership.talonDatasetKey || ''}
                    onChange={(e) => setEditingDealership({ ...editingDealership, talonDatasetKey: e.target.value })}
                  />
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                    Your Dealer ID is provided by Talon Powersports Solutions. Contact your Talon rep if you need help finding it.
                  </p>
                </div>
              )}

              {/* Reporting Section */}
              <div className="form-group" style={{ padding: '0' }}>
                <h3 style={{ marginTop: '0', marginBottom: '0.5rem', color: '#00d4ff', fontSize: '18px', fontWeight: '600' }}>
                  Reporting
                </h3>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editingDealership.showUnclosedFI !== false}
                    onChange={(e) => setEditingDealership({
                      ...editingDealership,
                      showUnclosedFI: e.target.checked
                    })}
                    style={{ marginTop: '3px', width: '18px', height: '18px', flexShrink: 0 }}
                  />
                  <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px', lineHeight: '1.5' }}>
                    Always show F&amp;I in charts
                    <span style={{ display: 'block', color: 'rgba(255,255,255,0.5)' }}>
                      Include saved F&amp;I entries in dashboards and reports as soon as they're logged.
                      Turn this off to only count deals once they're marked closed.
                    </span>
                  </span>
                </label>
              </div>

              {/* Dropdown Configuration Section */}
              <div className="form-group" style={{ padding: '0', borderRadius: '8px' }}>
                <h3 style={{ marginTop: '0', marginBottom: '0.5rem', color: '#00d4ff', fontSize: '18px', fontWeight: '600' }}>
                  Dropdown Configuration
                </h3>
                <p style={{ marginBottom: '1rem', color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: '1.5' }}>
                  Customize dropdown options for F&I input forms (lenders, providers, motorcycle models, etc.)
                </p>
                <DropdownManager dealershipId={editingDealership.id} talonEnabled={editingDealership.betaFeatures?.talonApi === true} />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        onConfirm={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        confirmStyle="primary"
        hideCancel={true}
        confirmText="OK"
      />
    </div>
  );
}

export default Dealerships;
